import type {
  MeetingTemplateKind,
  MeetingTemplateScope,
} from '@wundo/shared/meetingTemplate.ts';
import {
  DEFAULT_MEETING_TEMPLATE_EDITOR_LABELS,
  type MeetingTemplateEditorLabels,
} from './meeting-template-editor-labels.ts';
import type {
  MeetingTemplateCohortOption,
  MeetingTemplateDraft,
  MeetingTemplateTrackOption,
} from './meeting-template-types.ts';
import { MeetingTemplateView } from './meeting-template-view.tsx';
import { TemplateForm } from './template-form.tsx';

export interface MeetingTemplateEditorProps {
  /** The template being edited, or null while writing a new one. */
  template: MeetingTemplateDraft | null;
  /**
   * The tracks and cohorts it can be aimed at. Both absent or empty leaves the
   * field off: a personal template, or an organisation without tracks or
   * cohorts, has nothing to pick.
   */
  tracks?: MeetingTemplateTrackOption[];
  cohorts?: MeetingTemplateCohortOption[];
  /**
   * The organisation's default of a kind, which every member falls back to:
   * where the audience field would be, a line says it applies to everyone.
   */
  wholeOrganisationOnly?: boolean;
  /** Shows the template as text: a reader who may not change it. */
  readOnly?: boolean;
  /**
   * The kind of meeting the template is for: the starting choice on a new one,
   * the fixed value on an existing one. Absent, the editor says nothing about kind.
   */
  kind?: MeetingTemplateKind;
  /** Whether `kind` may still be chosen: only while writing a new template. */
  kindEditable?: boolean;
  /**
   * Offers who a new template is for, starting from this answer. Absent, the
   * editor says nothing about it and hands none back. The audience field only
   * shows while the answer is the organisation.
   */
  scope?: MeetingTemplateScope;
  /** Offers delete, behind a confirmation. Never on a new template. */
  canDelete?: boolean;
  /** Keeps the delete off and says why, e.g. on the default of a kind. */
  deleteRefusal?: string;
  saving?: boolean;
  /** Ready to show under the buttons: the host decides what went wrong. */
  error?: string;
  onSave: (draft: MeetingTemplateDraft) => void;
  /** Runs once the delete is confirmed; the dialog closes when it settles. */
  onDelete?: () => void | Promise<void>;
  /** Without it there is no back button: the host draws its own way out. */
  onCancel?: () => void;
  labels?: Partial<MeetingTemplateEditorLabels>;
}

/**
 * One meeting template (gesprekstype), whole: its name, its colour, and the
 * ordered topics every conversation of this type follows. Remount it (a `key`)
 * to start from another template.
 */
export function MeetingTemplateEditor({
  template,
  tracks = [],
  cohorts = [],
  wholeOrganisationOnly = false,
  readOnly = false,
  kind,
  kindEditable = false,
  scope,
  canDelete = false,
  deleteRefusal,
  saving = false,
  error,
  onSave,
  onDelete,
  onCancel,
  labels,
}: MeetingTemplateEditorProps) {
  const text = { ...DEFAULT_MEETING_TEMPLATE_EDITOR_LABELS, ...labels };

  if (readOnly && template) {
    return (
      <MeetingTemplateView
        template={template}
        kind={kind}
        onBack={onCancel}
        labels={text}
      />
    );
  }

  return (
    <TemplateForm
      template={template}
      tracks={tracks}
      cohorts={cohorts}
      wholeOrganisationOnly={wholeOrganisationOnly}
      labels={text}
      kind={kind}
      kindEditable={kindEditable}
      scope={scope}
      canDelete={canDelete}
      deleteRefusal={deleteRefusal}
      saving={saving}
      error={error}
      onSave={onSave}
      onDelete={onDelete}
      onCancel={onCancel}
    />
  );
}
